import { useParams } from "react-router-dom";
import {
  Container,
  MUITypography,
  MUIGrid,
  Divider,
  Button,
  useDialog,
} from "@cobee/cobee-ui";
import auth0Client from "app/auth/Auth";
import CenterLoader from "v2/ui/components/CenterLoader";
import { useTranslation } from "react-i18next";
import {
  PaymentType,
  RequestStates,
} from "v2/domain/benefitRequest/entities/Request";
import useBenefitRequestDetail from "../../hooks/queries/useBenefitRequestDetail";
import DetailRow from "../../components/DetailRow";
import styles from "./styles";
import useApproveBenefitRequest from "../../hooks/mutations/useApproveBenefitRequest";
import FinishedRequestInfo from "../../components/FinishedRequestInfo";
import RejectRequestDialog from "../../components/RejectRequestDialog";

type Params = {
  requestId: string;
};

export default function RequestDetailPage() {
  const { requestId } = useParams<Params>();
  const companyId = auth0Client.getCompanyId() as string;
  const { t } = useTranslation();

  const { title, buttonContainer } = styles;

  const { data, isFetching } = useBenefitRequestDetail({
    companyId,
    requestId,
  });

  const approveMutation = useApproveBenefitRequest();

  const contributionTypeDictionary = {
    [PaymentType.PUNCTUAL]: t("v2.common.punctual"),
    [PaymentType.RECURRENT]: t("v2.common.recurring"),
  };

  const { openDialog, dialogProps, closeDialog } = useDialog();

  return (
    <Container paddingStyle="32px 40px">
      {isFetching ? (
        <CenterLoader />
      ) : (
        <>
          <MUITypography variant="h6" sx={title}>
            {t("v2.benefit-requests.details-of-the-contribution-request")}
          </MUITypography>
          <DetailRow
            label={t("v2.common.name")}
            value={`${data?.employee?.name} ${data?.employee?.surname}` || ""}
          />
          <DetailRow
            label={t("v2.common.legal-id")}
            value={data?.employee?.legalId || ""}
          />
          <DetailRow
            label={t("v2.common.email")}
            value={data?.employee?.email || ""}
          />
          <Divider margin={12} />
          <DetailRow
            label={t("v2.common.benefit")}
            value={t(`v2.common.benefit-type.${data?.category}`) || ""}
          />
          <DetailRow
            label={t("v2.common.type-of-contribution")}
            value={contributionTypeDictionary[data?.type as string] || ""}
          />
          <DetailRow
            label={t("v2.common.amount")}
            value={`${data?.amount.toUnitary()}€` || ""}
          />
          <Divider margin={12} />
          <DetailRow
            label={t("v2.common.application-date")}
            value={data?.initialDate.toLocaleDateString() || ""}
          />
          {data && data?.state !== RequestStates.PENDING && (
            <FinishedRequestInfo data={data} />
          )}
          {data?.state === RequestStates.PENDING && (
            <>
              <MUIGrid container spacing={2} sx={buttonContainer}>
                <MUIGrid item>
                  <Button
                    variant="contained"
                    loading={approveMutation.isLoading}
                    onClick={() =>
                      approveMutation.mutate({ companyId, requestId })
                    }
                  >
                    {t("v2.common.approve")}
                  </Button>
                </MUIGrid>

                <MUIGrid item>
                  <Button
                    variant="outlined"
                    color="secondary"
                    disabled={approveMutation.isLoading}
                    onClick={() => openDialog()}
                  >
                    {t("v2.common.reject")}
                  </Button>
                </MUIGrid>
              </MUIGrid>
              <RejectRequestDialog
                requestId={requestId}
                companyId={companyId}
                benefit={data.category}
                dialogProps={dialogProps}
                closeDialog={closeDialog}
              />
            </>
          )}
        </>
      )}
    </Container>
  );
}
